/**
 * AlertHistoryPanel (OCP-390) — per-server health-alert incident timeline.
 * Pairs with GET /v1/admin/vps-servers/:id/alerts (host_alert_event). Shows
 * when each alert opened, when it resolved (or "ongoing"), how long it
 * lasted, and the specific reason it fired with.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

interface AlertRow {
  id: string;
  reason: string;
  opened_at: string;
  resolved_at: string | null;
  last_notified_at: string;
}

function fmtWhen(iso: string): string {
  return fmtDateTime(iso);
}

function fmtDuration(fromIso: string, toIso: string | null): string {
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  const mins = Math.max(0, Math.round((to - from) / 60_000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h < 24 ? `${h}h ${m}m` : `${Math.floor(h / 24)}d ${h % 24}h`;
}

export function AlertHistoryPanel({ serverId }: { serverId: string }) {
  const [rows, setRows] = useState<AlertRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ items: AlertRow[] }>(`/v1/admin/vps-servers/${serverId}/alerts`);
      setRows(r.items);
    } catch (e) {
      setError(e instanceof ApiError ? `Could not load alerts (HTTP ${e.status}).` : 'Could not load alerts.');
    }
  }, [serverId]);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-200">Alert history</h2>
      <div className="rounded border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        {error && (
          <div role="alert" className="px-4 py-2 text-xs text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {!rows && !error && <p className="px-4 py-3 text-sm text-gray-500">Loading…</p>}
        {rows && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-gray-500">
            No health alerts recorded — this server has stayed within limits.
          </p>
        )}
        {rows && rows.length > 0 && (
          <ul className="divide-y divide-gray-200 dark:divide-gray-800">
            {rows.map((a) => {
              const ongoing = a.resolved_at == null;
              return (
                <li key={a.id} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                  <span
                    className={`mt-1 inline-block h-2 w-2 shrink-0 rounded-full ${ongoing ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                    title={ongoing ? 'ongoing' : 'resolved'}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-gray-800 dark:text-gray-200">{a.reason}</div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {fmtWhen(a.opened_at)}
                      {ongoing ? (
                        <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-red-800 dark:bg-red-900/40 dark:text-red-300">
                          ongoing · {fmtDuration(a.opened_at, null)}
                        </span>
                      ) : (
                        <span className="ml-2 text-gray-400">
                          → {fmtWhen(a.resolved_at!)} · lasted {fmtDuration(a.opened_at, a.resolved_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
